import {
    Users,
    QrCode,
    Wallet,
    LineChart,
    Banknote,
    ShoppingCart,
    UserPlus,
    Tag,
    Receipt,
    CalendarCheck,
    BellRing,
    ShieldCheck,
    type LucideIcon,
} from "lucide-react";

// One card in the feature grid — kept data-only so the grid just maps over it.
export type FeatureCard = {
    icon: LucideIcon;
    title: string;
    description: string;
};

// The full module line-up shown in the compact "everything included" grid.
export const FEATURE_CARDS: FeatureCard[] = [
    {
        icon: Users,
        title: "Members & CRM",
        description:
            "Profiles, plans, trainer assignment and the full lead → member → renewal lifecycle in one place.",
    },
    {
        icon: CalendarCheck,
        title: "Attendance & Check-in",
        description:
            "One-second front-desk check-in, calendar history, auto check-out and busiest-day insights.",
    },
    {
        icon: QrCode,
        title: "Billing & Invoices",
        description:
            "PDF invoices with scannable QR codes, partial payments and receipts emailed automatically.",
    },
    {
        icon: LineChart,
        title: "Financials & P&L",
        description:
            "A real profit & loss statement, revenue-vs-expense trends, ARPM, churn and retention.",
    },
    {
        icon: Banknote,
        title: "Payroll",
        description:
            "Monthly staff salaries with automatic leave deductions and a clear net-payable breakdown.",
    },
    {
        icon: ShoppingCart,
        title: "POS & Products",
        description:
            "Sell supplements and merch with a fast cart, stock tracking, returns and refunds.",
    },
    {
        icon: UserPlus,
        title: "Leads & Follow-up",
        description:
            "Capture leads, track NEW → TRIAL → CONVERTED and never miss a follow-up reminder.",
    },
    {
        icon: Tag,
        title: "Offers & Promotions",
        description:
            "Time-limited percentage or flat discounts that apply automatically at enrollment.",
    },
    {
        icon: Receipt,
        title: "Expenses",
        description:
            "Log one-off and recurring bills, categorise spend and see upcoming dues at a glance.",
    },
    {
        icon: Wallet,
        title: "Leave Management",
        description:
            "Staff request leave, owners approve, and approved days flow straight into payroll.",
    },
    {
        icon: BellRing,
        title: "Notifications & Email",
        description:
            "In-app alerts plus automated emails for receipts, renewals, birthdays and daily digests.",
    },
    {
        icon: ShieldCheck,
        title: "Audit Logs & Roles",
        description:
            "Role-based access for owner, admin and trainer with a full who-did-what audit trail.",
    },
];

// A large alternating showcase block, each paired with its own coded mock UI.
export type ShowcaseBlock = {
    id: "members" | "billing" | "financials" | "attendance" | "payroll" | "pos";
    eyebrow: string;
    title: string;
    description: string;
    bullets: string[];
};

// The marquee feature stories told down the page, in narrative order.
export const SHOWCASE_BLOCKS: ShowcaseBlock[] = [
    {
        id: "members",
        eyebrow: "Members & plans",
        title: "Every member, plan and renewal in one place",
        description:
            "From first enquiry to active member, see who's on which plan, who their trainer is and exactly when they're due to renew — so nothing slips through the cracks.",
        bullets: [
            "Full profile with plan, trainer and join date",
            "Renewal dates surfaced before a membership lapses",
            "The whole lead → member → renewal lifecycle tracked",
        ],
    },
    {
        id: "billing",
        eyebrow: "Billing & invoices",
        title: "Get paid faster, look professional doing it",
        description:
            "Record a payment and GymMart instantly produces a branded PDF invoice — complete with a QR code members can scan to verify — and emails the receipt for you.",
        bullets: [
            "Branded PDF invoices with embedded QR verification",
            "Partial payments with live balance tracking",
            "Receipts auto-emailed the moment you record a payment",
        ],
    },
    {
        id: "financials",
        eyebrow: "Financials & analytics",
        title: "Know exactly how your gym is doing",
        description:
            "A real profit & loss statement, not just a revenue number. Watch income, expenses and net profit move month over month, broken down by plan and payment method.",
        bullets: [
            "Monthly P&L with month-over-month deltas",
            "Revenue-vs-expense trend and revenue by plan",
            "ARPM, churn and retention tracked automatically",
        ],
    },
    {
        id: "attendance",
        eyebrow: "Attendance",
        title: "Check members in before they reach the rack",
        description:
            "Search a name, tap, done. Every check-in feeds a visual calendar and a 90-day analysis of your busiest days so you can staff the floor with confidence.",
        bullets: [
            "Search-based one-second check-in / check-out",
            "Visual attendance calendar per member",
            "Busiest-day and consistency analytics",
        ],
    },
    {
        id: "payroll",
        eyebrow: "Payroll",
        title: "Run payroll in a click — leave included",
        description:
            "Set a monthly salary and GymMart handles the rest: per-day rate, approved leave deductions and net payable, all reconciled against what's already been paid.",
        bullets: [
            "Automatic leave deductions from approved requests",
            "Per-day rate and net-payable computed for you",
            "Payment history with balance-due tracking",
        ],
    },
    {
        id: "pos",
        eyebrow: "Point of sale",
        title: "A storefront built into your front desk",
        description:
            "Sell supplements, apparel and add-ons without leaving the app. A fast cart, member lookup, stock levels and refunds — with every sale rolled into your financials.",
        bullets: [
            "Fast cart with member lookup and discounts",
            "Live stock tracking and easy restocks",
            "Returns and refunds that update your P&L",
        ],
    },
];
